import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AftercareRenderer from "@/components/AftercareRenderer";

/* ── Aftercare-content renderer (matches the client portal exactly) ─────── */
function RenderContent({ text }) {
  return <AftercareRenderer text={text} />;
}

/* ── Editor modal ─────────────────────────────────────────────────────── */
function Editor({ form, allTreatments, onClose, onSaved }) {
  const isNew = !form?.id;
  const [title, setTitle] = useState(form?.title || "");
  const [content, setContent] = useState(form?.content || "");
  const [selected, setSelected] = useState(new Set((form?.treatment_ids) || []));
  const [tab, setTab] = useState("edit");
  const [saving, setSaving] = useState(false);
  const [tSearch, setTSearch] = useState("");

  const filteredTx = useMemo(() => {
    const q = tSearch.trim().toLowerCase();
    return (allTreatments || []).filter((t) =>
      !q || t.name.toLowerCase().includes(q) || (t.category || "").toLowerCase().includes(q)
    );
  }, [allTreatments, tSearch]);

  const grouped = useMemo(() => {
    const g = {};
    filteredTx.forEach((t) => { (g[t.category || "Uncategorised"] ||= []).push(t); });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredTx]);

  const toggle = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };

  const save = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const body = { title: title.trim(), content, treatment_ids: [...selected] };
      const r = isNew
        ? await api.post("/admin/aftercare-forms", body)
        : await api.patch(`/admin/aftercare-forms/${form.id}`, body);
      toast.success(isNew ? "Aftercare form created" : "Aftercare form updated");
      onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete “${form.title}”? Every treatment currently linked to this form will lose its aftercare content and become “No Aftercare Form Assigned”.`)) return;
    try {
      await api.delete(`/admin/aftercare-forms/${form.id}`);
      toast.success("Aftercare form deleted");
      onSaved(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-auto" data-testid="aftercare-editor-modal">
      <div className="bg-white w-full max-w-5xl my-8 shadow-xl border hairline">
        <header className="px-6 py-4 border-b hairline flex items-center justify-between">
          <div>
            <p className="mono-label text-stone-500">{isNew ? "New" : "Editing"}</p>
            <h2 className="font-serif text-2xl text-stone-900">{isNew ? "New Aftercare Form" : form.title}</h2>
          </div>
          <button onClick={onClose} data-testid="aftercare-editor-close" className="mono-label text-stone-500 hover:text-stone-900">Close ×</button>
        </header>

        <nav className="px-6 pt-4 flex gap-6 border-b hairline">
          {["edit", "preview"].map((k) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`aftercare-editor-tab-${k}`}
              className={`py-3 mono-label border-b-2 ${tab === k ? "border-stone-900 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-900"}`}>
              {k === "edit" ? "Edit" : "Preview"}
            </button>
          ))}
        </nav>

        {tab === "edit" ? (
          <div className="p-6 grid gap-6 md:grid-cols-3">
            <div className="md:col-span-2 space-y-4">
              <label className="block">
                <span className="mono-label text-stone-600">Form title</span>
                <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="aftercare-title"
                  className="mt-1 w-full border hairline px-3 py-2 text-[15px]" placeholder="e.g. Lip Filler Aftercare" />
              </label>
              <label className="block">
                <span className="mono-label text-stone-600">Content</span>
                <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={22} data-testid="aftercare-content"
                  className="mt-1 w-full border hairline px-3 py-2 text-[14px] font-mono leading-[1.6]"
                  placeholder={`Headings, paragraphs and bullet lines are all supported.\n\nStart bullet lines with • or - or *\n\n• First point\n• Second point`} />
                <p className="mt-1 text-xs text-stone-500">
                  Formatting: blank line = paragraph break · lines starting with <code>•</code>, <code>*</code>, <code>-</code> render as bullets.
                </p>
              </label>
            </div>

            <div className="space-y-3">
              <div>
                <span className="mono-label text-stone-600">Connected treatments</span>
                <p className="text-xs text-stone-500 mt-1">
                  Select every treatment this form applies to. A treatment can only be attached to one form — selecting it here will move it automatically.
                </p>
              </div>
              <input value={tSearch} onChange={(e) => setTSearch(e.target.value)} data-testid="aftercare-tx-search"
                className="w-full border hairline px-3 py-2 text-[13px]" placeholder="Search treatments…" />
              <div className="border hairline max-h-[430px] overflow-auto" data-testid="aftercare-tx-list">
                {grouped.length === 0 && <p className="p-3 text-sm text-stone-500 italic">No treatments match.</p>}
                {grouped.map(([cat, list]) => (
                  <div key={cat} className="border-b hairline last:border-b-0">
                    <p className="px-3 py-2 mono-label bg-stone-50 text-stone-600">{cat}</p>
                    {list.map((t) => (
                      <label key={t.id} className="flex items-center gap-2 px-3 py-2 border-t hairline first:border-t-0 hover:bg-stone-50 cursor-pointer">
                        <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggle(t.id)}
                          data-testid={`aftercare-tx-toggle-${t.id}`} />
                        <span className="text-[13px] text-stone-900">{t.name}</span>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
              <p className="text-xs text-stone-500" data-testid="aftercare-tx-count">
                {selected.size} treatment{selected.size === 1 ? "" : "s"} selected
              </p>
            </div>
          </div>
        ) : (
          <div className="p-6 bg-[#FAF9F6]">
            <div className="max-w-2xl mx-auto bg-white border hairline p-8">
              <p className="mono-label text-stone-500">Preview</p>
              <h3 className="font-serif text-3xl text-stone-900 mt-1 mb-6" data-testid="aftercare-preview-title">
                {title || "Untitled form"}
              </h3>
              <div data-testid="aftercare-preview-content"><RenderContent text={content} /></div>
            </div>
          </div>
        )}

        <footer className="px-6 py-4 border-t hairline flex items-center justify-between bg-stone-50">
          <div>
            {!isNew && (
              <button onClick={remove} data-testid="aftercare-editor-delete"
                className="mono-label text-red-700 hover:underline">Delete form</button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} data-testid="aftercare-editor-cancel"
              className="mono-label px-4 py-2 border hairline text-stone-700">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="aftercare-editor-save"
              className="mono-label px-6 py-2 bg-stone-900 text-white disabled:opacity-60">
              {saving ? "Saving…" : (isNew ? "Create form" : "Save changes")}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

/* ── Main list view ───────────────────────────────────────────────────── */
export default function AdminAftercareForms() {
  const [state, setState] = useState({ forms: [], orphans: [], loading: true });
  const [treatments, setTreatments] = useState([]);
  const [search, setSearch] = useState("");
  const [treatmentFilter, setTreatmentFilter] = useState("");
  const [editing, setEditing] = useState(null);   // null = closed, {} = new, {id,…} = existing

  const reload = async () => {
    setState((s) => ({ ...s, loading: true }));
    const [forms, tx] = await Promise.all([
      api.get("/admin/aftercare-forms"),
      api.get("/treatments"),
    ]);
    setState({ forms: forms.data.forms, orphans: forms.data.orphaned_treatments, loading: false });
    setTreatments(tx.data);
  };
  useEffect(() => { reload(); }, []);

  const seed = async () => {
    if (!window.confirm("Import existing aftercare copy from your treatments into forms? This only runs once (does nothing if you already have forms).")) return;
    try {
      const r = await api.post("/admin/aftercare-forms/_seed");
      if (r.data?.seeded) toast.success(`Imported ${r.data.forms_created} form${r.data.forms_created === 1 ? "" : "s"}`);
      else toast.info(`Skipped — you already have ${r.data.existing_forms} form(s).`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Seed failed"); }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return state.forms.filter((f) => {
      if (treatmentFilter && !(f.treatments || []).some((t) => t.id === treatmentFilter)) return false;
      if (!q) return true;
      const hay = [f.title, f.content, ...(f.treatments || []).map((t) => t.name)].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [state.forms, search, treatmentFilter]);

  return (
    <div className="space-y-6" data-testid="aftercare-forms-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-3xl text-stone-900">Aftercare Forms</h2>
          <p className="text-sm text-stone-600 mt-1">Manage the guidance that clients receive on their personalised aftercare page after a completed appointment.</p>
        </div>
        <div className="flex items-center gap-2">
          {state.forms.length === 0 && !state.loading && (
            <button onClick={seed} data-testid="aftercare-seed"
              className="mono-label px-4 py-2 border hairline text-stone-700">Import from treatments</button>
          )}
          <button onClick={() => setEditing({})} data-testid="aftercare-new"
            className="mono-label px-4 py-2 bg-stone-900 text-white">+ New Aftercare Form</button>
        </div>
      </div>

      <div className="flex gap-3 flex-wrap">
        <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="aftercare-search"
          className="border hairline px-3 py-2 text-sm w-72" placeholder="Search title, treatment or content…" />
        <select value={treatmentFilter} onChange={(e) => setTreatmentFilter(e.target.value)} data-testid="aftercare-filter"
          className="border hairline px-3 py-2 text-sm">
          <option value="">All treatments</option>
          {treatments.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      {state.loading && <p className="text-sm text-stone-500">Loading…</p>}

      {!state.loading && visible.length === 0 && (
        <div className="border hairline bg-white p-8 text-center" data-testid="aftercare-empty-state">
          <p className="mono-label text-stone-500">No forms match</p>
          <p className="text-sm text-stone-600 mt-2">
            {state.forms.length === 0
              ? "Create your first aftercare form to link it to one or more treatments."
              : "Adjust your search or clear the treatment filter."}
          </p>
        </div>
      )}

      {!state.loading && visible.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2" data-testid="aftercare-form-grid">
          {visible.map((f) => (
            <button key={f.id} onClick={() => setEditing(f)} data-testid={`aftercare-row-${f.id}`}
              className="text-left bg-white border hairline p-5 hover:border-stone-900 transition-colors">
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="font-serif text-xl text-stone-900">{f.title}</h3>
                <span className="mono-label text-stone-500">
                  {(f.treatments || []).length} treatment{(f.treatments || []).length === 1 ? "" : "s"}
                </span>
              </div>
              {(f.treatments || []).length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`aftercare-row-${f.id}-treatments`}>
                  {f.treatments.map((t) => (
                    <span key={t.id} className="text-[11px] px-2 py-1 bg-stone-100 text-stone-700 border hairline">
                      {t.name}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mt-3 text-xs text-amber-700 mono-label">Not linked to any treatment</p>
              )}
              <p className="mt-3 text-xs text-stone-500 line-clamp-2">
                {(f.content || "").slice(0, 200) || "No content yet."}
              </p>
            </button>
          ))}
        </div>
      )}

      {!state.loading && state.orphans.length > 0 && (
        <div className="mt-8 border hairline bg-amber-50 p-5" data-testid="aftercare-orphans">
          <p className="mono-label text-amber-900">Treatments with no aftercare form assigned</p>
          <p className="text-sm text-amber-900/80 mt-1 mb-3">
            Clients who complete these treatments will not receive an aftercare portal email. Link them to a form below.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {state.orphans.map((t) => (
              <span key={t.id} data-testid={`aftercare-orphan-${t.id}`}
                className="text-[11px] px-2 py-1 bg-white border hairline text-stone-700">
                {t.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {editing !== null && (
        <Editor
          form={editing}
          allTreatments={treatments}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}
